// src/pages/TestSession.js
import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { fetchQuestions, submitTest } from "../api";

export default function TestSession() {
  const navigate = useNavigate();
  const config = JSON.parse(sessionStorage.getItem("testConfig") || "{}");

  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [currIdx, setCurrIdx] = useState(0);
  const [responses, setResponses] = useState({}); // { questionId: { selectedOptionIndex, timeTakenSeconds } }
  const [marked, setMarked] = useState(new Set()); // Set of question IDs
  const [showDrawer, setShowDrawer] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const startTimeRef = useRef(Date.now());
  const qStartTimeRef = useRef(Date.now());

  useEffect(() => {
    loadQuestions();
  }, []);

  async function loadQuestions() {
    setLoading(true);
    try {
      const data = await fetchQuestions(config.subject, "", "", config.limit || 10, config.tag || "");
      setQuestions(data);
      startTimeRef.current = Date.now();
      qStartTimeRef.current = Date.now();
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  }

  function handleSelect(oi) {
    const q = questions[currIdx];
    const now = Date.now();
    const timeSpent = Math.round((now - qStartTimeRef.current) / 1000);

    // Update response
    setResponses(prev => ({
      ...prev,
      [q._id]: {
        questionId: q._id,
        selectedOptionIndex: oi,
        timeTakenSeconds: (prev[q._id]?.timeTakenSeconds || 0) + timeSpent
      }
    }));

    qStartTimeRef.current = now; // Reset timer for this question (though user stays here for now)
  }

  function goTo(idx) {
    // Record time for current question before moving
    const q = questions[currIdx];
    const now = Date.now();
    const timeSpent = Math.round((now - qStartTimeRef.current) / 1000);

    setResponses(prev => ({
      ...prev,
      [q._id]: {
        ...prev[q._id],
        questionId: q._id,
        timeTakenSeconds: (prev[q._id]?.timeTakenSeconds || 0) + timeSpent
      }
    }));

    setCurrIdx(idx);
    qStartTimeRef.current = now;
  }

  function toggleMark() {
    const qId = questions[currIdx]._id;
    setMarked(prev => {
      const next = new Set(prev);
      if (next.has(qId)) next.delete(qId);
      else next.add(qId);
      return next;
    });
  }

  async function handleSubmit() {
    // Final time recording
    const q = questions[currIdx];
    const now = Date.now();
    const timeSpent = Math.round((now - qStartTimeRef.current) / 1000);

    const finalResponses = {
      ...responses,
      [q._id]: {
        ...responses[q._id],
        questionId: q._id,
        timeTakenSeconds: (responses[q._id]?.timeTakenSeconds || 0) + timeSpent
      }
    };

    setSubmitting(true);
    try {
      // Map responses object to array as expected by backend
      const responseArray = questions.map(qu => {
        const resp = finalResponses[qu._id];
        return {
          questionId: qu._id,
          selectedOptionIndex: resp ? resp.selectedOptionIndex : null,
          timeTakenSeconds: resp ? resp.timeTakenSeconds : 0
        };
      });

      const res = await submitTest(
        config.testType || "diagnostic",
        config.subject || "",
        responseArray,
        new Date(startTimeRef.current).toISOString()
      );

      sessionStorage.setItem("testResult", JSON.stringify(res));
      navigate("/test/result");
    } catch (e) {
      setError("Submission failed: " + e.message);
      setSubmitting(false);
    }
  }

  if (loading) return <div className="loading"><div className="spinner" /><span>Loading test questions…</span></div>;
  if (error && questions.length === 0) return <div className="error-box">{error} <button onClick={loadQuestions}>Retry</button></div>;

  const q = questions[currIdx];
  const selected = responses[q?._id]?.selectedOptionIndex;
  const isMarked = q && marked.has(q._id);

  return (
    <div style={{ maxWidth: 900, margin: "0 auto" }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <div>
          <h1 className="page-title">
            {config.testType === "mock" ? config.tag : (config.testType === "sectional" ? `${config.subject} Sectional` : "Diagnostic Test")}
          </h1>
          <p className="page-sub">Question {currIdx + 1} of {questions.length}</p>
        </div>
        <button className="btn btn-dark" onClick={handleSubmit} disabled={submitting}>
          {submitting ? "Submitting..." : "Finish Test"}
        </button>
      </div>

      {error && <div className="error-box" style={{ marginBottom: 16 }}>{error}</div>}

      <div className="two-col" style={{ gridTemplateColumns: "1fr minmax(auto, 240px)", alignItems: "start" }}>
        {/* Main Card */}
        <div className="card">
          {q && (
            <>
              <div style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: 10 }}>
                {q.subject} &middot; {q.topic}
              </div>
              <div style={{ fontSize: 16, lineHeight: 1.6, marginBottom: 24, color: "var(--text-primary)" }}>
                {q.questionText}
              </div>

              {q.options.map((opt, i) => (
                <div
                  key={i}
                  className={`option ${selected === i ? "selected" : ""}`}
                  onClick={() => handleSelect(i)}
                >
                  <div className="opt-label">{["A", "B", "C", "D"][i]}</div>
                  {opt}
                </div>
              ))}
            </>
          )}

          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 24 }}>
            <button
              className="btn btn-sm"
              disabled={currIdx === 0}
              onClick={() => goTo(currIdx - 1)}
            >
              ← Previous
            </button>
            <button
              className={`btn btn-sm ${isMarked ? "btn-primary" : ""}`}
              style={{ background: isMarked ? "#602ba1" : "", color: isMarked ? "white" : "" }}
              onClick={toggleMark}
            >
              🚩 {isMarked ? "Added for Preview" : "Add for Preview"}
            </button>
            <button
              className="btn btn-sm"
              disabled={currIdx === questions.length - 1}
              onClick={() => goTo(currIdx + 1)}
            >
              Next →
            </button>
          </div>
        </div>

        {/* Sidebar: Question Navigator */}
        <div className="card">
          <div className="card-title" style={{ fontSize: 13, marginBottom: 12 }}>Question Navigator</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8 }}>
            {questions.map((_, i) => {
              const qId = questions[i]._id;
              const isAnswered = responses[qId]?.selectedOptionIndex !== undefined;
              const isCurrent = i === currIdx;
              const isMarked = marked.has(qId);

              let bg = "var(--bg)";
              let color = "var(--text-secondary)";
              let border = "1px solid var(--border)";

              if (isCurrent) {
                border = "2px solid var(--blue)";
                color = "var(--blue)";
              } else if (isMarked) {
                bg = "#602ba1";
                color = "white";
                border = "1px solid #4a1f7d";
              } else if (isAnswered) {
                bg = "var(--blue-bg)";
                color = "var(--blue)";
                border = "1px solid var(--blue-light)";
              }

              return (
                <button
                  key={i}
                  onClick={() => goTo(i)}
                  style={{
                    height: 40,
                    borderRadius: 8,
                    background: bg,
                    color: color,
                    border: border,
                    fontSize: 14,
                    fontWeight: 600,
                    cursor: "pointer",
                    transition: "all 0.2s"
                  }}
                >
                  {i + 1}
                </button>
              );
            })}
          </div>

          <div style={{ marginTop: 20, paddingTop: 16, borderTop: "1px solid var(--border)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 11, color: "var(--text-muted)", marginBottom: 6 }}>
              <div style={{ width: 12, height: 12, borderRadius: 3, background: "#602ba1", border: "1px solid #4a1f7d" }}></div>
              Marked for Review
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 11, color: "var(--text-muted)", marginBottom: 6 }}>
              <div style={{ width: 12, height: 12, borderRadius: 3, background: "var(--blue-bg)", border: "1px solid var(--blue-light)" }}></div>
              Answered
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 11, color: "var(--text-muted)" }}>
              <div style={{ width: 12, height: 12, borderRadius: 3, background: "var(--bg)", border: "1px solid var(--border)" }}></div>
              Unvisited/Unanswered
            </div>
          </div>
        </div>
      </div>

      {/* Floating Review List Button - Stacked above the global Notes icon */}
      <button
        className="notes-fab"
        style={{ background: "#602ba1", right: 24, bottom: (marked.size > 0 ? 94 : -100), transition: "bottom 0.4s" }}
        onClick={() => setShowDrawer(true)}
        title="View Marked Questions"
      >
        🚩
        {marked.size > 0 && (
          <span style={{ position: "absolute", top: -5, right: -5, background: "var(--red)", color: "white", fontSize: 10, padding: "2px 6px", borderRadius: 10 }}>
            {marked.size}
          </span>
        )}
      </button>

      {/* Review List Drawer */}
      <div className={`notepad-drawer ${showDrawer ? "open" : ""}`} style={{ width: 340 }}>
        <div className="notepad-header" style={{ background: "#602ba1", color: "white" }}>
          <div style={{ fontWeight: 600 }}>Marked for Preview</div>
          <button className="notepad-close" style={{ color: "white" }} onClick={() => setShowDrawer(false)}>×</button>
        </div>
        <div className="notepad-body" style={{ padding: 0 }}>
          {marked.size === 0 ? (
            <div style={{ padding: 30, textAlign: "center", color: "var(--text-muted)" }}>
              No questions marked for preview yet.
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column" }}>
              {questions.map((qu, i) => {
                if (!marked.has(qu._id)) return null;
                return (
                  <div
                    key={qu._id}
                    onClick={() => { goTo(i); setShowDrawer(false); }}
                    style={{
                      padding: "16px 20px",
                      borderBottom: "1px solid var(--border-soft)",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      background: i === currIdx ? "var(--blue-bg)" : "transparent"
                    }}
                  >
                    <div style={{ width: 28, height: 28, borderRadius: "50%", background: "#602ba1", color: "white", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, flexShrink: 0 }}>
                      {i + 1}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                        {qu.subject} · {qu.topic}
                      </div>
                      <div style={{ fontSize: 13, fontWeight: 500, color: "var(--text-primary)", lineHeight: 1.4, marginBottom: 4 }}>
                        {qu.questionText.length > 80 ? qu.questionText.substring(0, 80) + "..." : qu.questionText}
                      </div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                        {qu.options.slice(0, 2).map((opt, oi) => (
                          <div key={oi} style={{ fontSize: 10, background: "#f1f5f9", padding: "2px 6px", borderRadius: 4, color: "#64748b" }}>
                            {String.fromCharCode(65 + oi)}: {opt.substring(0, 20)}...
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <div className="notepad-footer">
          <div style={{ fontSize: 11, color: "var(--text-muted)" }}>{marked.size} item(s) to review</div>
          <button className="btn btn-sm" onClick={() => setShowDrawer(false)}>Close</button>
        </div>
      </div>
    </div>
  );
}
